import {
  ProviderUnavailableError,
  type ProviderCallOptions,
  type ScreenColor,
  type VisionDecision,
  type VisionProvider,
  type VisionRequest,
  type VisionVerdict,
} from "@loumy/contracts";

// §7 `vision`-Provider → fal.ai-Aggregator (D50/D52-Input-Gate, D57). Bezieht ein VLM über
// die fal.ai-`openrouter/router/vision`-API mit DEMSELBEN `FAL_API_KEY` wie der video-Slot
// (kein zweiter Key — Aggregator-Idee D57). ECHTER Adapter, ENV-gated (ohne Key → wirft).
//
// Dokumentierter Contract (fal.ai/models/openrouter/router/vision/api, verifiziert 2026-06-01):
//   Auth   : Header `Authorization: Key <FAL_API_KEY>`.
//   Sync   : POST {base}/{endpoint}  (JSON) → blockiert bis fertig → { output: string, usage }
//            (fal.run = synchroner Endpoint; für ein kurzes VLM-Urteil kein Poll nötig).
//   Inputs : image_urls (list<string>, REQUIRED), prompt (REQUIRED), system_prompt (opt),
//            model (REQUIRED, OpenRouter-Format z.B. `google/gemini-2.5-flash`),
//            temperature (0 = deterministisch), max_tokens.
//   Output : { output: string } — wir lassen das VLM STRIKTES JSON antworten und parsen den
//            Verdict pass|edit|reject (D50). Kein SSRF-Pfad: image_url ist DATEN an fal (wird
//            NICHT von uns gefetcht); der Endpoint ist fixe Config (keine fremde URL).

const FAL_RUN_BASE_URL = "https://fal.run";
const FAL_VISION_ENDPOINT = "openrouter/router/vision";
// Verifiziertes Beispiel-Modell des Routers; günstig/schnell, für ein pass/edit/reject-Urteil
// ausreichend. Über `model` konfigurierbar (z.B. `anthropic/claude-3.5-sonnet`).
const DEFAULT_VLM = "google/gemini-2.5-flash";

const VERDICTS = new Set<VisionDecision>(["pass", "edit", "reject"]);
const SCREEN_COLORS = new Set<ScreenColor>(["green", "blue"]);

// Gate-Instruktion (D50/D52/D76): Eingabebild ↔ Prompt/Aktion → Eignung als I2V-Start-Frame,
// bei loop=true ZUSÄTZLICH Loop-Feasibility. EXPORTIERT als Funktion, damit ALLE vision-Provider
// (fal & Gemini, D60) dieselbe Instruktion nutzen — und der loop-Zusatz nur bei Bedarf erscheint.
// D114: jeder Array-Eintrag ist eine VOLLE Zeile/Bullet, gejoint mit "\n" — das frühere join(" ")
// verklebte die Bullets zu EINEM Fließtext-Absatz und zerstörte die Kriterien-Struktur fürs VLM.
const VISION_GATE_BASE = [
  "You are the input-validation gate of a sprite-sheet animation generator.",
  // D83: das Tool animiert JEDES Motiv — Charakter/Kreatur ODER Objekt/Prop/Effekt/Item/Icon
  // (Truhe, Kanone, Münze, Fackel, Flagge, UI-Icon …), NICHT nur Figuren.
  "The subject can be ANYTHING — a character or creature, OR an object, prop, effect, item or icon (a treasure chest, a cannon, a spinning coin, a torch flame, a flag, a UI icon).",
  "Given ONE input image and the user's prompt/action, decide whether the image is usable as the START FRAME for image-to-video animation.",
  // D84: PERMISSIV — die Eingabepose/-Details sind evtl. die GEWOLLTE Wahl des Users. Default = 'pass'
  // für jedes plausible, stabile Bild; 'edit' NUR wenn etwas die Nutzung wirklich blockt oder dem
  // Prompt widerspricht. KEINE „kanonische" Pose um ihrer selbst willen erzwingen.
  "Be PERMISSIVE — default to 'pass' for any plausible, stable image; the input pose and details may be the user's deliberate choice. Verdicts:",
  '- "pass": one clear subject, usable as-is — INCLUDING a deliberate pose or detail (hands in pockets, a held item, a particular stance). Do NOT demand a \'canonical\' pose for its own sake.',
  '- "edit": something genuinely BLOCKS use and a MINIMAL re-pose/clean of the SAME subject fixes it — cropped/off-centre, messy background, OR the pose directly CONTRADICTS the prompt/action.',
  '- "reject": fundamentally unusable — no clear subject, multiple subjects, or policy-violating.',
];
// D76/D83/D84: bei einem geforderten LOOP muss der Zustand einen nahtlosen Zyklus tragen (first==last)
// — aber NUR das blockierende Element bewerten, plausible Details bewahren (D84).
const VISION_GATE_LOOP = [
  "LOOP REQUIRED: also judge LOOP FEASIBILITY — the subject's state must support a seamless cycle where the FIRST and LAST frame are the SAME image (the motion starts at this state and returns to it).",
  "- A TRANSIENT or EXTREME state CANNOT be a loop anchor: a creature MID-AIR / at the APEX of a jump, a chest frozen HALF-OPEN, a cannon mid-recoil, or anything at the fully-extended extreme of a motion can never loop → verdict 'edit' (re-pose to a STABLE state the motion can return to: a grounded stance for a creature, a closed chest, a cannon at rest, a clean canonical orientation for a spinning coin/icon).",
  // D114 (Konvergenz): die Edit-Instruktion (input-normalize) re-posed Beine für walk/run in eine
  // GEERDETE Schrittpose — das Gate MUSS diese Pose als validen Anker durchlassen, sonst fordert
  // das Re-Validate „neutral rest" zurück, der Editor liefert wieder Schrittpose … Ping-Pong bis
  // maxEdits → Job-Fail. Edit-Ziel und Pass-Kriterium sind jetzt DERSELBE Zustand.
  "- A grounded mid-stride CONTACT stance (one foot ahead of the other, both feet on or near the ground) IS a stable, VALID loop anchor for a walk or run — mark it 'pass', do NOT call it transient.",
  // D84: nur das von der Bewegung GEFORDERTE Element prüfen; plausible Details (Hände in Taschen)
  // sind KEIN Loop-Problem — Gehen loopt mit Händen in Taschen.
  "- Otherwise judge ONLY the element the requested motion actually needs, and PASS the rest. FOR LEGGED CREATURES the LEGS must be able to stride (legs together/static while the action is a walk/run → 'edit' the LEGS); these leg criteria do NOT apply to objects, props or icons.",
  "- A plausible detail (hands in pockets, a held item, a particular stance) is NOT a loop problem — a walk loops fine with hands in pockets: KEEP them, do NOT mark 'edit' for them. Only mark 'edit' for a detail if it DIRECTLY blocks the requested motion OR CONTRADICTS the prompt (e.g. the prompt says 'arms swinging' but the hands are in pockets).",
  "- The subject must be fully visible and centred. If a loop problem is fixable by RE-POSING the SAME subject → 'edit'; only if fundamentally unusable → 'reject'.",
];
// D79: empfohlene Chroma-Key-Screen-Farbe — KONTRASTIEREND zum Motiv, damit der Greenscreen-Key
// den Charakter nicht mit herauslöst (z.B. grüner Hoodie auf Greenscreen).
// D92: der Output wird chroma-gekeyt → der Input braucht eine SAUBERE, kontrastierende Screen-
// Fläche. „Kein green/blue-Screen" ODER „Subjekt teilt die Screen-Farbe" ist NICHT sauber keybar
// → 'edit' (normalize re-screent das Motiv auf die kontrastierende Farbe). Orthogonal zur Pose (D84).
// D114: Clash-Kriterium OBJEKTIVIERT — nicht jedes „ähnliche" Farbgefühl, sondern nur wenn der Key
// SICHTBAR große Subjektteile löschen würde (nahezu identischer Farbton auf großen Flächen).
const VISION_GATE_SCREEN = [
  "CHROMA SCREEN (required — the output is chroma-keyed): the subject MUST sit on a SOLID, UNIFORM GREEN or BLUE screen whose colour CLEARLY CONTRASTS with the subject, so the background keys out cleanly without erasing any part of the subject. Mark 'edit' if EITHER:",
  "(a) there is NO solid green/blue screen — the background is photographic, cluttered, textured, or any other colour (it must be replaced with a clean contrasting screen); OR",
  "(b) keying the screen colour would VISIBLY ERASE the subject: LARGE parts of the subject are nearly the SAME hue as the screen (e.g. a green-clad subject on a green screen). Small accents or merely vaguely-similar shades do NOT count — only a clash that would leave obvious holes. Then the screen must be CHANGED to the contrasting colour.",
  "This screen check is INDEPENDENT of the pose: a perfectly posed subject on a non-keyable background (or a colour-clashing screen) is still 'edit'.",
  // D115: Wahrnehmung (VLM) von Entscheidung (Code) getrennt — das VLM meldet, OB das Motiv
  // prominente grüne/blaue Flächen zeigt (subjectGreen/subjectBlue); die kontrastierende Farbe
  // leitet `resolveVisionVerdict` DETERMINISTISCH ab (grün→blue, blau→green). Vorher war die
  // Farbwahl eine reine VLM-Empfehlung — ein Lite-VLM verpatzte die Kontrast-Regel real (grüner
  // Screen hinter extrem grünem Motiv), und seit dem D114-Pin blieb die falsche Erstwahl fixiert.
  'SUBJECT COLOURS: report whether the SUBJECT ITSELF (ignore the current background entirely) shows prominent GREEN areas (green clothing, skin, foliage — large or central, not tiny accents) as "subjectGreen", and prominent BLUE areas as "subjectBlue".',
  'SCREEN COLOR: also recommend the backdrop colour that CONTRASTS with the subject so keying never erases it — prominent green on the subject → "blue"; prominent blue → "green"; otherwise "green".',
];
// D114: Re-Validate-Kontext (input-normalize-Schleife) — das Bild wurde SOEBEN von der Pipeline
// editiert. Ohne diesen Block prüfte das Gate den Editor-Output mit der vollen Erstprüfungs-Strenge
// („pure saturated/uniform"), während Qwen-Edit real leichte Verläufe/Artefakte rendert → das frisch
// editierte Bild fiel am eigenen Anspruch wieder durch (Nicht-Konvergenz → prepared=false → Job-Fail).
const VISION_GATE_REVALIDATE = [
  "RE-VALIDATION: this image was ALREADY edited by this pipeline to satisfy the rules above.",
  "Judge pragmatically and CONVERGE: if the remaining issues are merely cosmetic — slight screen unevenness or gradient, mild compression artifacts, minor lighting imperfections — the verdict is 'pass'.",
  "Only mark 'edit' again for a SUBSTANTIVE problem that genuinely blocks animation or keying; never re-litigate a detail the rules above allow.",
];
// D114: von der Pipeline FIXIERTE Screen-Farbe — enhance/key/loop lesen dieselbe D79-ScalarRef
// (validate.keyColor). Empfähle das Re-Validate die ANDERE Farbe, schriebe die Edit-Instruktion
// trotzdem wieder die gepinnte → deterministische Endlosschleife. Das Gate urteilt deshalb NUR
// über Sauberkeit/Lösch-Risiko und gibt die gepinnte Farbe zurück.
function visionGateScreenPin(c: ScreenColor): string {
  return [
    `SCREEN COLOUR IS FIXED: the pipeline has pinned the screen colour to ${c.toUpperCase()}.`,
    `Do NOT recommend switching colours — always return "keyColor":"${c}".`,
    "Judge only whether the screen is clean/solid and whether keying it would visibly erase large parts of the subject; if it genuinely would, say so in the reason, but keep the pinned keyColor.",
  ].join("\n");
}
const VISION_GATE_TAIL = [
  'Respond with ONLY a compact JSON object, no markdown, no prose: {"verdict":"pass"|"edit"|"reject","reason":"<short>","keyColor":"green"|"blue","subjectGreen":true|false,"subjectBlue":true|false}.',
];

/** D114: Kontext fürs Gate — loop-Feasibility (D76), Re-Validate-Modus und gepinnte Screen-Farbe
 *  (beides aus der input-normalize-Schleife). */
export interface VisionGateOptions {
  loop?: boolean;
  revalidate?: boolean;
  screenColor?: ScreenColor;
}

/** Gate-System-Prompt; bei loop=true mit dem Loop-Feasibility-Zusatz (D76). D79: stets mit der
 *  Screen-Farb-Empfehlung (kontrastierend zum Motiv). D114: optionaler Re-Validate-Block +
 *  gepinnte Screen-Farbe; boolean-Signatur bleibt rückwärtskompatibel (loop-only). */
export function visionGateSystem(opts: boolean | VisionGateOptions = false): string {
  const o: VisionGateOptions = typeof opts === "boolean" ? { loop: opts } : opts;
  return [
    ...VISION_GATE_BASE,
    ...(o.loop ? VISION_GATE_LOOP : []),
    ...VISION_GATE_SCREEN,
    ...(o.screenColor ? [visionGateScreenPin(o.screenColor)] : []),
    ...(o.revalidate ? VISION_GATE_REVALIDATE : []),
    ...VISION_GATE_TAIL,
  ].join("\n");
}

/** Rückwärtskompatibler Default (loop=false) — bestehende Importe/Tests. */
export const VISION_GATE_SYSTEM_PROMPT = visionGateSystem(false);

export interface FalVisionConfig {
  apiKey?: string;
  baseUrl?: string;
  /** fal-Endpoint (Default `openrouter/router/vision`). */
  endpoint?: string;
  /** Zugrundeliegendes VLM im OpenRouter-Format (Default `google/gemini-2.5-flash`). */
  model?: string;
  /** Unparsebarer VLM-Output → `pass` statt `reject`? Default `false` (fail-closed, D52). */
  failOpen?: boolean;
  /** `edit`-Verdict zulassen? Nur sinnvoll mit echtem image-edit-Provider (§13). Default `false`. */
  allowEdit?: boolean;
}

/**
 * Parst den VLM-Output zu einem Verdict. STRIKT: erstes JSON-Objekt extrahieren, `verdict`
 * gegen die erlaubte Menge prüfen. Gibt **`null`** zurück, wenn nichts Gültiges geparst werden
 * kann — der Aufrufer (`judge`) entscheidet dann fail-closed (`reject`, Default) bzw. fail-open
 * (`pass`, opt-in via `failOpen`). KEIN stilles Default-Verdict hier (D52-Gate-Sicherheit).
 */
export function parseVisionVerdict(output: string): {
  verdict: VisionDecision;
  reason?: string;
  keyColor?: ScreenColor;
  subjectGreen?: boolean;
  subjectBlue?: boolean;
} | null {
  const start = output.indexOf("{");
  const end = output.lastIndexOf("}");
  if (start >= 0 && end > start) {
    try {
      const obj = JSON.parse(output.slice(start, end + 1)) as {
        verdict?: unknown;
        reason?: unknown;
        keyColor?: unknown;
        subjectGreen?: unknown;
        subjectBlue?: unknown;
      };
      if (typeof obj.verdict === "string" && VERDICTS.has(obj.verdict as VisionDecision))
        return {
          verdict: obj.verdict as VisionDecision,
          reason: typeof obj.reason === "string" ? obj.reason : undefined,
          // D79: nur eine valide Farbe übernehmen; sonst undefined → stromabwärts Default green.
          keyColor:
            typeof obj.keyColor === "string" && SCREEN_COLORS.has(obj.keyColor as ScreenColor)
              ? (obj.keyColor as ScreenColor)
              : undefined,
          // D115: Wahrnehmungs-Booleans (Motiv zeigt prominente grüne/blaue Flächen) — nur echte
          // Booleans übernehmen; alles andere undefined (kein implizites false aus Müll-Werten).
          subjectGreen: typeof obj.subjectGreen === "boolean" ? obj.subjectGreen : undefined,
          subjectBlue: typeof obj.subjectBlue === "boolean" ? obj.subjectBlue : undefined,
        };
    } catch {
      // unparsebar → null
    }
  }
  return null;
}

/**
 * D115: deterministische Screen-Farb-Ableitung aus den Wahrnehmungs-Booleans — Wahrnehmung ist
 * VLM-Stärke, Regelanwendung ist Code-Stärke (ein Lite-VLM verpatzte die Kontrast-Regel real):
 * Motiv prominent grün (und nicht blau) → `blue`; prominent blau (und nicht grün) → `green`;
 * beides/keines → die VLM-Empfehlung als Tiebreaker (beide Farben gleich (un)problematisch),
 * sonst null (stromabwärts Default green, D79).
 */
export function deriveKeyColor(parsed: {
  keyColor?: ScreenColor;
  subjectGreen?: boolean;
  subjectBlue?: boolean;
}): ScreenColor | null {
  const g = parsed.subjectGreen === true;
  const b = parsed.subjectBlue === true;
  if (g && !b) return "blue";
  if (b && !g) return "green";
  return parsed.keyColor ?? null;
}

/**
 * Finalisiert einen VLM-Output zu einem Verdict-Kern — GEMEINSAM für ALLE vision-Provider
 * (fal & Gemini, D60), damit die Gate-Semantik (D52) provider-unabhängig identisch ist:
 *  - unparsebar/ungültig → fail-CLOSED `reject` (`failOpen` opt-in → `pass`),
 *  - `edit` ohne echten image-edit-Provider (input-normalize, §13) → `pass` (`allowEdit` opt-in),
 *  - `rawPreview` hart gekappt (Datenschutz/Größe; kein ungekappter VLM-Output in den Signals),
 *  - D115: `keyColor` = CODE-Ableitung aus den Wahrnehmungs-Booleans (deriveKeyColor) statt
 *    blindem Vertrauen in die VLM-Empfehlung; `pinnedScreenColor` (D114-Re-Validate) erzwingt
 *    die gepinnte Farbe CODE-seitig (der Prompt-Hinweis allein ist keine Garantie).
 */
export function resolveVisionVerdict(
  output: string,
  opts: { failOpen?: boolean; allowEdit?: boolean; pinnedScreenColor?: ScreenColor } = {},
): {
  verdict: VisionDecision;
  reason: string | null;
  verdictRaw: VisionDecision | null;
  rawPreview: string;
  keyColor: ScreenColor | null;
  keyColorRaw: ScreenColor | null;
  subjectGreen: boolean | null;
  subjectBlue: boolean | null;
} {
  const parsed = parseVisionVerdict(output);
  let verdict: VisionDecision;
  let reason: string;
  if (!parsed) {
    verdict = opts.failOpen ? "pass" : "reject";
    reason = "parse_error";
  } else {
    verdict = parsed.verdict;
    reason = parsed.reason ?? "";
    if (verdict === "edit" && !opts.allowEdit) {
      reason = `edit→pass (kein image-edit-Provider, §13): ${reason}`.trim();
      verdict = "pass";
    }
  }
  // D115: gepinnte Farbe gewinnt IMMER (Re-Validate, D114) — sonst Code-Ableitung aus der
  // Wahrnehmung; die rohe VLM-Empfehlung bleibt als keyColorRaw fürs Debugging sichtbar.
  const keyColor = opts.pinnedScreenColor ?? (parsed ? deriveKeyColor(parsed) : null);
  return {
    verdict,
    reason: reason || null,
    verdictRaw: parsed?.verdict ?? null,
    rawPreview: output.slice(0, 500),
    keyColor,
    keyColorRaw: parsed?.keyColor ?? null,
    subjectGreen: parsed?.subjectGreen ?? null,
    subjectBlue: parsed?.subjectBlue ?? null,
  };
}

export class FalVisionProvider implements VisionProvider {
  readonly id = "fal-vision";
  readonly version = 1;
  readonly displayName = "fal.ai (OpenRouter Vision)";

  private readonly baseUrl: string;
  private readonly endpoint: string;
  private readonly model: string;
  private readonly failOpen: boolean;
  private readonly allowEdit: boolean;

  constructor(private readonly config: FalVisionConfig = {}) {
    this.baseUrl = (config.baseUrl ?? FAL_RUN_BASE_URL).replace(/\/+$/, "");
    this.endpoint = config.endpoint ?? FAL_VISION_ENDPOINT;
    this.model = config.model ?? DEFAULT_VLM;
    this.failOpen = config.failOpen === true;
    this.allowEdit = config.allowEdit === true;
  }

  private requireKey(): string {
    if (!this.config.apiKey)
      throw new ProviderUnavailableError("vision", this.id, "FAL_API_KEY fehlt (Adapter nicht konfiguriert, §13).");
    return this.config.apiKey;
  }

  async judge(req: VisionRequest, opts?: ProviderCallOptions): Promise<VisionVerdict> {
    const apiKey = this.requireKey();
    if (!req.imageUrl)
      throw new ProviderUnavailableError("vision", this.id, "Vision-Gate braucht ein Eingabebild (imageUrl fehlt).");

    const userPrompt = `User prompt: ${req.prompt}${req.action ? `\nAction: ${req.action}` : ""}${
      req.loop ? "\nLoop required: yes (judge loop feasibility)" : ""
    }\nJudge the attached image.`;
    const res = await fetch(`${this.baseUrl}/${this.endpoint}`, {
      method: "POST",
      headers: { Authorization: `Key ${apiKey}`, "Content-Type": "application/json" },
      signal: opts?.signal, // D76: abbrechbar bei Timeout/Cancel (Re-Validate-Schleife)
      body: JSON.stringify({
        model: this.model,
        image_urls: [req.imageUrl],
        prompt: userPrompt,
        // D114: Re-Validate-Kontext + gepinnte Screen-Farbe der Edit-Schleife fließen ins Gate.
        system_prompt: visionGateSystem({
          loop: req.loop === true,
          revalidate: req.revalidate === true,
          ...(req.screenColor ? { screenColor: req.screenColor } : {}),
        }),
        temperature: 0, // deterministisch (D34-Geist: gleiches Bild/Prompt → gleiches Urteil)
        max_tokens: 200,
      }),
    });
    if (!res.ok) throw new Error(`fal vision: HTTP ${res.status} ${res.statusText}`);
    const body = (await res.json()) as { output?: string };
    const output = typeof body.output === "string" ? body.output : "";

    const { verdict, reason, verdictRaw, rawPreview, keyColor, keyColorRaw, subjectGreen, subjectBlue } =
      resolveVisionVerdict(output, {
        failOpen: this.failOpen,
        allowEdit: this.allowEdit,
        // D115/D114: gepinnte Re-Validate-Farbe CODE-erzwingen (Prompt-Hinweis allein reicht nicht).
        ...(req.screenColor ? { pinnedScreenColor: req.screenColor } : {}),
      });
    return {
      verdict,
      // D115: keyColorRaw/subjectGreen/subjectBlue im score → Live-Debugging der Farb-Ableitung.
      score: { model: this.model, endpoint: this.endpoint, verdictRaw, reason, rawPreview, keyColor, keyColorRaw, subjectGreen, subjectBlue },
      ...(keyColor ? { keyColor } : {}), // D79
    };
  }
}
